import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ITicket } from '@/interfaces/tickets/ITicket';
import { ticketsService } from '@/services/tickets.service';
import { notificationService } from '@/services/notification.service';
import { useAuth } from '@/hooks/useAuth';
import { Modal } from '@/components/common/Modal';
import { cookieService } from '@/services/cookie.service';
import { StoredCookies } from '@/interfaces/auth/cookies.enum';

export const TicketDetail = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<ITicket | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSendingReminder, setIsSendingReminder] = useState(false);
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [recipientNote, setRecipientNote] = useState('');
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const [isOwner, setIsOwner] = useState(false);

  useEffect(() => {
    if (uuid) loadTicket(uuid);
  }, [uuid]);

  useEffect(() => {
    if (!isAuthLoading && !isAuthenticated) {
      setShowRegisterModal(true);
    }
  }, [isAuthenticated, isAuthLoading]);

  useEffect(() => {
    if (ticket && isAuthenticated) {
      const currentUsername = cookieService.getCookie(StoredCookies.USERNAME);
      setIsOwner(currentUsername === ticket.user.username);
    }
  }, [ticket, isAuthenticated]);

  const loadTicket = async (ticketUuid: string) => {
    try {
      const response = await ticketsService.getTicketById(ticketUuid);
      const { attributes } = response.data;
      setTicket({
        ...attributes,
        uuid: attributes.uuid.toString()
      });
    } catch (error) {
      if (error instanceof Error) {
        notificationService.error(error.message);
      } else {
        notificationService.error('Error loading the ticket');
      }
      navigate('/tickets');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!ticket?.uuid) return;

    setIsDownloading(true);
    try {
      const blob = await ticketsService.downloadTicket(ticket.uuid);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ticket-${ticket.eventName}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      notificationService.error('Error downloading ticket');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSendReminder = async () => {
    if (!ticket?.uuid) return;

    setIsSendingReminder(true);
    try {
      await ticketsService.sendReminder(ticket.uuid);
      notificationService.success('Reminder sent successfully');
    } catch (error) {
      if (error instanceof Error) {
        notificationService.error(error.message);
      } else {
        notificationService.error('Error sending reminder');
      }
    } finally {
      setIsSendingReminder(false);
    }
  };

  const handleUpdateRecipientNote = async () => {
    if (!ticket?.uuid) return;

    try {
      await ticketsService.updateRecipientNote(ticket.uuid, recipientNote);
      await loadTicket(ticket.uuid);
      setIsEditingNote(false);
      notificationService.success('Note updated successfully');
    } catch (error) {
      if (error instanceof Error) {
        notificationService.error(error.message);
      } else {
        notificationService.error('Error updating note');
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="loading-message">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0F9015]"></div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]" data-testid="ticket-not-found">
        <p className="text-xl text-[#084509]">Ticket not found</p>
        <button
          onClick={() => navigate('/tickets')}
          className="mt-4 bg-[#C9FD05] hover:opacity-90 transition-opacity"
        >
          Back to list
        </button>
      </div>
    );
  }

  return (
    <>
      <Modal
        isOpen={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        title="Register to access more features"
      >
        <div className="p-4">
          <p className="mb-4">Register now to access all ticket features and manage your tickets more effectively.</p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowRegisterModal(false)}
              className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
            >
              Maybe later
            </button>
            <button
              onClick={() => navigate('/auth/sign-up')}
              className="px-4 py-2 bg-[#0F9015] text-white rounded-lg hover:opacity-90"
            >
              Register now
            </button>
          </div>
        </div>
      </Modal>
      <div className="container mx-auto px-4 py-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
            <div className="mb-4 md:mb-0">
              <div className="flex items-center gap-3">
                <h1 className="text-[#0F9015] text-4xl mb-2" data-testid="ticket-title">{ticket.eventName}</h1>
                {!ticket.file && (
                  <span 
                    className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold bg-amber-100 text-amber-800 border border-amber-200"
                    data-testid="ticket-unreleased-badge"
                  >
                    <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                    </svg>
                    Unreleased
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/tickets')}
                className="bg-[#C9FD05] hover:opacity-90 transition-opacity flex items-center px-6 py-2 rounded-lg"
                data-testid="back-button"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back
              </button>
              {isAuthenticated && isOwner && (
                <>
                  <button
                    onClick={() => navigate(`/tickets/${uuid}/edit`)}
                    className="bg-[#C9FD05] hover:opacity-90 transition-opacity flex items-center px-6 py-2 rounded-lg"
                    data-testid="edit-button"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit Ticket
                  </button>
                  <button
                    onClick={handleSendReminder}
                    disabled={isSendingReminder}
                    className="bg-[#0F9015] text-white hover:opacity-90 transition-opacity flex items-center px-6 py-2 rounded-lg disabled:opacity-50"
                    data-testid="send-reminder-button"
                  >
                    {isSendingReminder ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Email Ticket
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <div className="space-y-6">
                <h2 className="text-[#0F9015] text-4xl mb-6 font-heading">{ticket.eventName}</h2>
                
                <div>
                  <div className="text-[#0F9015] text-xl mb-1 font-heading">Price:</div>
                  <div className="text-[#084509] text-2xl font-body" data-testid="ticket-price">${ticket.price}</div>
                </div>

                <div>
                  <div className="text-[#0F9015] text-xl mb-1 font-heading">Status:</div>
                  <div className="text-[#084509] text-2xl font-body" data-testid="ticket-status">{ticket.payment_status}</div>
                </div>

                {ticket.email && (
                  <div>
                    <div className="text-[#0F9015] text-xl mb-1 font-heading">To:</div>
                    <div className="text-[#084509] text-2xl font-body" data-testid="ticket-email">{ticket.email}</div>
                  </div>
                )}

                {ticket.emmiter_note && (
                  <div>
                    <div className="text-[#0F9015] text-xl mb-1 font-heading">Issuer Note:</div>
                    <div className="text-[#084509] text-2xl font-body" data-testid="ticket-emmiter-note">{ticket.emmiter_note}</div>
                  </div>
                )}

                <div>
                  <div className="text-[#0F9015] text-xl mb-1 font-heading">Recipient Note:</div>
                  {isEditingNote ? (
                    <div className="flex flex-col gap-2">
                      <textarea
                        value={recipientNote}
                        onChange={(e) => setRecipientNote(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-lg font-body"
                        rows={3}
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={handleUpdateRecipientNote}
                          className="bg-[#0F9015] text-white px-4 py-2 rounded-lg hover:opacity-90 font-body"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setIsEditingNote(false)}
                          className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:opacity-90 font-body"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-between items-start" data-testid="recipient-note">
                      <div className="text-2xl font-body">
                        {ticket.recipient_note ? (
                          <span className="text-[#084509]">{ticket.recipient_note}</span>
                        ) : (
                          <span className="text-gray-400">You can leave a note here</span>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setRecipientNote(ticket.recipient_note || '');
                          setIsEditingNote(true);
                        }}
                        className="text-[#0F9015] hover:opacity-90"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {ticket.file && (
              <div className="bg-white rounded-xl shadow-sm p-8">
                <div className="flex flex-col items-center justify-center h-full">
                  <div className="w-full max-w-sm mx-auto">
                    <div className="text-center">
                      <div className="text-[#0F9015] text-4xl mb-6 font-heading">{ticket.eventName}</div>
                      <div className="border-t border-dashed border-gray-200 my-6"></div>
                      <div className="text-[#084509] text-2xl mb-8 font-body">${ticket.price}</div>
                      <button
                        onClick={handleDownloadPdf}
                        className="w-full bg-[#C9FD05] hover:opacity-90 transition-opacity flex items-center justify-center px-6 py-3 rounded-lg text-lg font-body"
                        data-testid="download-button"
                        disabled={isDownloading}
                      >
                        {isDownloading ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-black mr-2"></div>
                            Downloading...
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Download Ticket
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}; 